// Framework-agnostic MFE loading indicator.
//
// hrm-console is a single-spa shell that orchestrates MFEs built in different
// frameworks. It must not bundle a UI framework or design system into its own
// scope: a second copy of Vue/@knowbe4/codex leaks global styles (theme.css,
// styles.css) and duplicate component/CSS registrations that clash with MFEs
// shipping their own Codex (e.g. the settings-mfe radio-button conflict).
//
// This renders the animated KnowBe4 brand loader with plain DOM + CSS only.
// The SVG markup and wipe animation are ported 1:1 from @knowbe4/codex's
// KbLoader (type="brand"); the brand fill falls back to the vermillion brand
// token so a host-provided theme still wins when present.

const LOADER_STYLE_ID = 'hrm-console-mfe-loader';

// Loader-specific animation + color only. The `.mfe-loading` wrapper layout
// (centered absolute overlay) is owned by the shell's index.html.
const LOADER_CSS = `
.mfe-loading .kb-loader-icon { fill: var(--brand-icon, #ef4523); }
.mfe-loading .kb-loader-mask-circle {
  stroke-dasharray: 100;
  stroke-dashoffset: 100px;
  transform-origin: 33px 31px;
  transform: rotate(-90deg) scaleX(-1);
  animation: hrm-mfe-loader-wipe 3.5s cubic-bezier(0.215, 0.61, 0.355, 1) infinite;
}
@keyframes hrm-mfe-loader-wipe {
  0% { stroke-dashoffset: 100px; }
  50% { stroke-dashoffset: 0; }
  100% { stroke-dashoffset: -100px; }
}
@media (prefers-reduced-motion: reduce) {
  .mfe-loading .kb-loader-mask-circle { stroke-dashoffset: 0; animation: none; }
}
`;

let maskSeq = 0;

function ensureLoaderStyles(): void {
  if (document.getElementById(LOADER_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = LOADER_STYLE_ID;
  style.textContent = LOADER_CSS;
  document.head.appendChild(style);
}

function buildLoaderSvg(): string {
  // Unique mask id per instance so concurrent loaders never collide on `url(#…)`.
  // maskSeq is the only interpolated value and is a shell-controlled integer, so
  // this template never embeds untrusted input. The accessible label is applied
  // via setAttribute after insertion (see showMfeLoader), not string-interpolated.
  const maskId = `kb-loader-mask-${maskSeq++}`;
  return `<svg data-test-id="kb-loader" width="40" height="38.75" viewBox="0 0 64 62" fill="none" role="status" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <mask id="${maskId}">
        <rect width="64" height="62" fill="black"></rect>
        <circle class="kb-loader-mask-circle" cx="33" cy="31" r="25" pathLength="100" stroke="white" stroke-width="50" fill="none"></circle>
      </mask>
    </defs>
    <path data-test-id="kb-loader-path" d="M23.4749 53.155H0.574951L7.26494 46.545C4.52494 41.995 2.95494 36.655 2.95494 30.955C2.95494 14.215 16.4149 0.755005 33.1549 0.755005C49.8949 0.755005 63.4249 14.215 63.4249 30.955C63.4249 47.695 50.5049 60.745 34.2049 61.245L34.2649 47.975C43.2849 47.455 50.4349 39.975 50.4349 30.825C50.4349 21.675 42.7449 13.875 33.2549 13.875C23.7649 13.875 16.075 21.345 16.075 30.825C16.075 32.835 16.4249 34.775 17.0549 36.565L23.4849 30.225V53.185" class="kb-loader-icon" mask="url(#${maskId})"></path>
  </svg>`;
}

let loaderGate: Promise<void> = Promise.resolve();

export function setLoaderGate(p: Promise<void>): void {
  loaderGate = p;
}

const activeLoaders = new WeakMap<HTMLElement, HTMLDivElement>();
const pendingShows = new WeakMap<HTMLElement, { cancelled: boolean }>();

export async function showMfeLoader(host: HTMLElement): Promise<void> {
  if (activeLoaders.has(host)) hideMfeLoader(host);

  const token = { cancelled: false };
  pendingShows.set(host, token);

  await loaderGate;

  if (token.cancelled || pendingShows.get(host) !== token) return;
  pendingShows.delete(host);

  ensureLoaderStyles();

  const el = document.createElement('div');
  el.className = 'mfe-loading';
  el.innerHTML = buildLoaderSvg();
  // Set the accessible label via setAttribute rather than interpolating it into
  // the SVG string, so a future dynamic/localized label can never become an
  // innerHTML injection sink.
  el.querySelector('svg')?.setAttribute('aria-label', 'Loading');
  host.appendChild(el);

  activeLoaders.set(host, el);
}

export function hideMfeLoader(host: HTMLElement): void {
  const pending = pendingShows.get(host);
  if (pending) {
    pending.cancelled = true;
    pendingShows.delete(host);
  }

  const el = activeLoaders.get(host);
  if (!el) return;
  el.remove();
  activeLoaders.delete(host);
}
